import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';

import { environment } from '@env';

import { ToastService, ToastSeverity } from '../services/toast.service';

const SILENT_HEADER = 'X-Silent';
const SILENT_PATH_SUFFIXES = ['/auth/refresh', '/auth/me'];

interface EnvelopeNotification {
  message?: string;
  severity?: string;
}

interface EnvelopeError {
  message?: string;
  severity?: string;
}

function isValidSeverity(value: unknown): value is ToastSeverity {
  return value === 'info' || value === 'success' || value === 'warning' || value === 'error';
}

function isSilent(url: string, hasSilent: boolean): boolean {
  if (hasSilent) return true;
  if (!url.startsWith(environment.apiUrl)) return true;
  return SILENT_PATH_SUFFIXES.some((suffix) => url.endsWith(suffix));
}

function extractSuccess(body: unknown): { message: string; severity: ToastSeverity } | null {
  if (!body || typeof body !== 'object') return null;
  const notification = (body as { notification?: EnvelopeNotification }).notification;
  if (!notification?.message) return null;
  const severity = isValidSeverity(notification.severity) ? notification.severity : 'success';
  return { message: notification.message, severity };
}

function extractError(body: unknown): { message: string; severity: ToastSeverity } | null {
  if (!body || typeof body !== 'object') return null;
  const err = (body as { error?: EnvelopeError }).error;
  if (!err?.message) return null;
  const severity = isValidSeverity(err.severity) ? err.severity : 'error';
  return { message: err.message, severity };
}

export const toastInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const silent = isSilent(req.url, req.headers.has(SILENT_HEADER));

  return next(req).pipe(
    tap((event) => {
      if (silent || !(event instanceof HttpResponse)) return;
      const success = extractSuccess(event.body);
      if (success) toast.show(success.message, success.severity);
    }),
    catchError((err: unknown) => {
      if (silent) return throwError(() => err);
      // 401s are handled by the refresh/session flow (silent renewal or redirect to login with its
      // own message); only the login form surfaces its 401 (wrong credentials).
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        !req.url.endsWith('/auth/login')
      ) {
        return throwError(() => err);
      }
      if (err instanceof HttpErrorResponse) {
        const failure = extractError(err.error);
        if (failure) toast.show(failure.message, failure.severity);
      }
      return throwError(() => err);
    }),
  );
};
